import clsx from 'clsx';
import { debounce } from 'lodash';
import { FunctionComponent, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import Reveal from 'reveal.js';

import RewindWatermark from '@/components/RewindWatermark';
import Award from '@/components/sections/Award';
import Countries from '@/components/sections/Countries';
import IneligibleIntro from '@/components/sections/IneligibleIntro';
import Intro from '@/components/sections/Intro';
import KeyStats from '@/components/sections/KeyStats';
import Niche from '@/components/sections/Niche';
import Recommendations from '@/components/sections/Recommendations';
import RevealSection from '@/components/sections/RevealSection';
import Signin from '@/components/sections/SignIn';
import ThankYou from '@/components/sections/ThankYou';
import TopLanguage from '@/components/sections/TopLanguage';
import TopPercent from '@/components/sections/TopPercent';
import Welcome from '@/components/sections/Welcome';
import useIsMounted from '@/hooks/useIsMounted';
import useWindowHeightChanged from '@/hooks/useWindowHeightChanged';
import { useRevealJsContext } from '@/providers/RevealJsProvider';
import { AllFavourites } from '@/services/FavouritesApi';
import { RecommendationData } from '@/services/RecommendationApi';
import { RewindApiError, RewindData } from '@/services/RewindApi';
import DataLayer from '@/utils/DataLayer';

export interface UserData {
  firstname: string;
  userId: string;
}

interface RewindSlidesProps {
  isLoggedIn: boolean;
  userData?: UserData;
  recommendationData?: RecommendationData | null;
  rewindData?: RewindData | RewindApiError | null;
  favouritesData: AllFavourites;
}

const RewindSlides: FunctionComponent<RewindSlidesProps> = (props) => {
  const {
    userData, rewindData, recommendationData,
    isLoggedIn = false,
    favouritesData,
  } = props;
  const { sdk, setSdk } = useRevealJsContext();
  const [ready, setReady] = useState(false);
  const [currentSlideId, setCurrentSlideId] = useState('welcome');
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const isMounted = useIsMounted();
  const windowHeight = useWindowHeightChanged();
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

  // Will wait 3s before showing the loading message
  const delayShowLoadingIndicator = debounce(() => {
    setShowLoadingIndicator(true);
  }, 3000);

  // Inform RevealJS SDK that the DOM has changed
  useEffect(() => {
    sdk?.configure({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    sdk?.layout();
    sdk?.sync();

    const minViewportHeight = 900;
    let scaleRatio = 1;
    if (window.innerHeight < minViewportHeight) {
      scaleRatio = window.innerHeight / minViewportHeight;
    }

    // Taken from Reveal.js, it's not loaded because we are using their "disableLayout" option.
    // **********
    // On some mobile devices '100vh' is taller than the visible
    // viewport which leads to part of the presentation being
    // cut off. To work around this we define our own '--vh' custom
    // property where 100x adds up to the correct height.
    // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    if (isMobile) {
      // --vh is automatically used by Reveal's CSS
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    }

    document.documentElement.style.setProperty('--page-scale-ratio', `${scaleRatio}`);
  }, [windowHeight, recommendationData, rewindData, sdk, showLoadingIndicator]);

  // Upon successful sign-in, we change the DOM by replacing elements.
  // Thus, we need to tell RevealJS to come back to the current slide to refresh it.
  // This handles the change of the 2nd slide that can be either: a loading indicator, sign-in form or intro/ineligible page
  useEffect(() => {
    let timeoutHandler: ReturnType<typeof setTimeout>;

    if (rewindData || recommendationData || showLoadingIndicator) {
      timeoutHandler = setTimeout(() => {
        sdk?.slide(0, currentSlideIndex);
      }, 250);
    }

    return () => {
      clearTimeout(timeoutHandler);
    };
  }, [currentSlideIndex, rewindData, recommendationData, sdk, showLoadingIndicator]);

  useEffect(() => {
    if (!isLoggedIn && rewindData === undefined) {
      setTimeout(() => {
        sdk?.slide(0, 0);
      }, 200);
    }
  }, [isLoggedIn, rewindData, sdk]);

  useEffect(() => {
    const slideTransitionEndHandler = debounce(() => {
      sdk?.getCurrentSlide()?.classList.add('no-delay');
      sdk?.getPreviousSlide()?.classList.remove('no-delay');
    }, 200);

    window.addEventListener('transitionend', slideTransitionEndHandler);

    if (
      // Handles React v18's React Strict Mode multiple mounting
      (process.env.NODE_ENV !== 'development' || isMounted())
      && !sdk
    ) {
      const trackedScreens: string[] = [];
      const reveal = new Reveal({
        controls: false, // do not show the prev/next control arrows
        disableLayout: true, // disables automatic layout styles to use our own
        mouseWheel: true, // enables side scrolling using the mousewheel
        transition: 'none', // disables slide-in transition when navigating from slide to slide
        autoAnimate: true, // enables auto animation of matching HTML elements between slides
        overview: false, // disables overview mode (zoom out to see all slides) when pressing ESC
        // @ts-ignore, for some reason jumpToSlide type declaration is not exposed but the option is working
        jumpToSlide: false, // disables jump-to-slide UI (pressing G)
      });
      reveal.initialize();
      reveal.on('ready', () => {
        setReady(true);
        document.body.classList.remove('loading');
        document.body.classList.add('ready');
      });
      reveal.on('slidechanged', () => {
        const previousSlide = reveal.getPreviousSlide();
        const currentSlide = reveal.getCurrentSlide();
        const screenId = currentSlide.id;
        setCurrentSlideId(screenId);
        setCurrentSlideIndex(reveal.getIndices().v);

        currentSlide.ariaHidden = 'false';
        if (previousSlide) {
          previousSlide.ariaHidden = 'true';
        }

        if (!trackedScreens.includes(screenId)) {
          DataLayer.events.screenChange(screenId);
          trackedScreens.push(screenId);
        }
      });

      setSdk(reveal);
    }

    return (() => {
      window?.removeEventListener('transitionend', slideTransitionEndHandler);
      sdk?.destroy();
    });
  }, [isMounted, sdk, setSdk]);

  useEffect(() => {
    if (userData) {
      if (rewindData === undefined) {
        delayShowLoadingIndicator();
      } else {
        delayShowLoadingIndicator.cancel();
      }
    }
  }, [userData, rewindData, delayShowLoadingIndicator]);

  // When stacking multiple subsection inside on section, RevealJS will allow up/down and swipe up/down to scroll to the next subsection.
  // To allow navigation with swipe up/down, we are nesting all of our sections inside a parent one.
  // So our page structure is actually one main section with multiple subsections (our pages).
  return (
    <>
      <div
        className={clsx('reveal', { ready, loading: !ready, eligible: rewindData })}
        style={{
          height: 'var(--slide-height)',
        }}
      >
        <div className="slides pt-[60px]">
          <section className="vertical-section">
            <RevealSection id="welcome" aria-hidden={false}>
              <Welcome active={currentSlideId === 'welcome'}/>
            </RevealSection>
            {!userData && rewindData === undefined && (
              <RevealSection id="signin">
                <Signin/>
              </RevealSection>
            )}
            {userData && rewindData === undefined && (
              <RevealSection id="loading">
                {showLoadingIndicator && <div>Loading your data...</div>}
              </RevealSection>
            )}
            {(userData && rewindData === null) && (
              <RevealSection id="ineligible">
                <IneligibleIntro/>
              </RevealSection>
            )}
            {(userData && rewindData && 'error' in rewindData) && (
              <RevealSection id="error">
                <div>Sorry, there was an issue. Please try again later</div>
              </RevealSection>
            )}
            {(userData && rewindData && !('error' in rewindData)) && (
              <>
                <RevealSection id="intro">
                  <Intro firstname={rewindData.user.firstname}/>
                </RevealSection>
                {rewindData.consumption && (
                  <RevealSection id="keystats">
                    <KeyStats {...rewindData.consumption} active={currentSlideId === 'keystats'}/>
                  </RevealSection>
                )}
                {Object.keys(rewindData.consumptionPerCountry).length > 0 && (
                  <RevealSection id="countries">
                    <Countries
                      maxCountryConsumption={rewindData.maxCountryConsumption}
                      consumptionPerCountry={rewindData.consumptionPerCountry}
                      active={currentSlideId === 'countries'}
                    />
                  </RevealSection>
                )}
                {rewindData.topLanguage && (
                  <RevealSection id="toplanguage">
                    <TopLanguage topLanguage={rewindData.topLanguage} isPrimaryTopLanguage={rewindData.topLanguageType === 'primary'} active={currentSlideId === 'toplanguage'}/>
                  </RevealSection>
                )}
                <RevealSection id="nichemainstream">
                  <Niche active={currentSlideId === 'nichemainstream'} value={rewindData.nicheSlider ?? 0.0}/>
                </RevealSection>
                {rewindData?.award && (
                  <RevealSection id="award">
                    <Award award={rewindData.award} active={currentSlideId === 'award'}/>
                  </RevealSection>
                )}
                {!!rewindData.topPercentile && (
                  <RevealSection id="toppercent">
                    <TopPercent active={currentSlideId === 'toppercent'} topPercentile={rewindData.topPercentile} promoItems={rewindData.promoItems}/>
                  </RevealSection>
                )}
                {recommendationData?.items && (
                  <RevealSection id="recommendations">
                    <Recommendations active={currentSlideId === 'recommendations'} recommendedItems={recommendationData.items} favouritesData={favouritesData}/>
                  </RevealSection>
                )}
                {rewindData.consumption && (
                  <RevealSection id="thankyou">
                    <ThankYou shareImageUrl={rewindData.shareImageUrl} active={currentSlideId === 'thankyou'} sdkReady={ready}/>
                  </RevealSection>
                )}
              </>
            )}
          </section>
        </div>
      </div>
      {currentSlideId !== 'welcome' && (
        <RewindWatermark className="watermark fixed bottom-0 right-0 animate-slidefromright z-[5]"/>
      )}
    </>
  );
};

export default RewindSlides;
